import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { Box, Button, Card, CardContent, CardMedia, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { ICommonState, trackConversion } from "../constant";
import SectionHeading from "../Shared/SectionHeading";

const ServiceCards: React.FC<ICommonState> = ({
  services,
  setIsModalOpen,
  setSelectedService,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isServicePage = location.pathname === "/services";
  const prefersReducedMotion = useReducedMotion();

  return (
    <Box
      id="service-section"
      sx={{
        px: { xs: 1.2, sm: 1.8, md: 2.6 },
        py: { xs: 4.6, md: 5.8 },
        scrollMarginTop: { xs: "92px", md: "84px" },
        background: isServicePage
          ? "linear-gradient(180deg, #f7f2ed 0%, #fbf8f4 100%)"
          : "transparent",
      }}
    >
      <SectionHeading
        eyebrow="What We Offer"
        title="Interior Services Tailored to Your Space"
        subtitle="From residential transformations to commercial layouts, each offering blends aesthetics, functionality, and delivery-focused execution."
        titleAs={isServicePage ? "h1" : "h2"}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "none",
          mx: "auto",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: { xs: 1.4, sm: 1.7, md: 2 },
        }}
      >
        {(services || []).map((service: any, index: number) => (
          <motion.div
            key={service.service_name || index}
            initial={prefersReducedMotion ? false : { opacity: 0, y: 26, scale: 0.985 }}
            whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={
              prefersReducedMotion
                ? undefined
                : {
                    duration: 0.58,
                    delay: index * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }
            }
            whileHover={prefersReducedMotion ? undefined : { y: -7 }}
          >
            <Card
              sx={{
                height: "100%",
                borderRadius: "22px",
                overflow: "hidden",
                background:
                  "linear-gradient(155deg, rgba(255,255,255,0.9), rgba(255,248,245,0.72))",
                border: "1px solid rgba(128,51,65,0.14)",
                boxShadow: "0 16px 34px rgba(24,10,12,0.12)",
                transition: "transform 260ms ease, box-shadow 260ms ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 24px 42px rgba(24,10,12,0.16)",
                },
              }}
            >
              <Box sx={{ p: 1.1, pb: 0.3 }}>
                <CardMedia
                  component="img"
                  image={service.image_url}
                  alt={service.service_name}
                  loading="lazy"
                  sx={{
                    height: 220,
                    borderRadius: "16px",
                    objectFit: "cover",
                  }}
                  onContextMenu={(event) => event.preventDefault()}
                />
              </Box>

              <CardContent
                sx={{ px: 2, pt: 0.7, pb: "16px !important", minHeight: 212 }}
              >
                <Typography
                  component="h3"
                  sx={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontWeight: 700,
                    fontSize: { xs: "1.42rem", md: "1.65rem" },
                    color: "#32171c",
                    lineHeight: 1.12,
                  }}
                >
                  {service.service_name}
                </Typography>

                <Typography
                  sx={{
                    mt: 1.05,
                    color: "rgba(53,33,36,0.8)",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.9rem",
                    lineHeight: 1.72,
                    minHeight: 92,
                  }}
                >
                  {service.service_content}
                </Typography>

                <Button
                  onClick={() => {
                    setIsModalOpen(true);
                    (setSelectedService as any)(service.service_name);
                    trackConversion();
                  }}
                  endIcon={<ArrowOutwardRoundedIcon />}
                  sx={{
                    mt: 1.2,
                    borderRadius: "999px",
                    px: 1.8,
                    py: 0.8,
                    textTransform: "none",
                    fontFamily: '"Manrope", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    color: "#fff",
                    background:
                      "linear-gradient(137deg, #803341 0%, #a85156 100%)",
                    boxShadow: "0 10px 18px rgba(128,51,65,0.28)",
                    "&:hover": {
                      background:
                        "linear-gradient(137deg, #6f2c39 0%, #954349 100%)",
                    },
                  }}
                >
                  Get Proposal
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      {isServicePage ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3.2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate("/")}
            sx={{
              px: 2.6,
              py: 1.05,
              borderRadius: "999px",
              textTransform: "none",
              borderColor: "rgba(128,51,65,0.45)",
              color: "#672936",
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 700,
              "&:hover": {
                borderColor: "#803341",
                backgroundColor: "rgba(128,51,65,0.07)",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default ServiceCards;
